import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Settings, Clock, Save, Users, History, Moon, Sun, Trash2, Download, Loader2, CheckCircle2, AlertTriangle, Mail, ShieldCheck, CalendarOff, Send, Lock, Wifi, Languages, Database, HardDrive, Bell, Cloud, CloudOff, Info } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useSeason } from '@/contexts/SeasonContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import UsersPage from './UsersPage';
import AuditLogPage from './AuditLogPage';
import RolesPage from './RolesPage';
import ActiveSessionsPage from './ActiveSessionsPage';
import TranslationsPage from './TranslationsPage';

export default function AdminSettingsPage() {
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const { refresh: refreshSeason } = useSeason();
  const { hasPerm } = useAuth();
  const [settings, setSettings] = useState({ session_timeout_minutes: 15, allow_bulk_delete: false, enable_email_notifications: true, enable_2fa: true, season_open: true, email_new_request: true, email_request_reviewed: true, email_cancellation: true, email_user_invited: true, email_backup_failure: true, email_account_lockout: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cleanupConfirm, setCleanupConfirm] = useState(false);
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const runCleanup = async () => {
    setCleanupRunning(true);
    try {
      const r = await api.post('/api/sponsors/cleanup-archived-cascade');
      if (r.data.affected?.length > 0) {
        const names = r.data.affected.map(a => `${a.sponsor} (${a.requests_cancelled} req, ${a.rights_archived} rights)`).join(', ');
        toast.success(`Cleaned up: ${names}`);
      } else {
        toast.info('All archived sponsors are already clean — nothing to do.');
      }
      setCleanupConfirm(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setCleanupRunning(false); }
  };
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [seasons, setSeasons] = useState([]);
  const [newSeasonName, setNewSeasonName] = useState('');

  // Season import state
  const [importPreview, setImportPreview] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importSource, setImportSource] = useState('spl');

  const [importSyncEnabled, setImportSyncEnabled] = useState(true);

  // Database backup state
  const [backupStatus, setBackupStatus] = useState(null);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupDownloading, setBackupDownloading] = useState(false);
  const [backupCountdown, setBackupCountdown] = useState(0);

  const loadBackupStatus = async () => {
    try {
      const r = await api.get('/api/admin/backup/status');
      setBackupStatus(r.data);
      if (r.data.seconds_remaining > 0) setBackupCountdown(r.data.seconds_remaining);
    } catch { /* status endpoint optional */ }
  };

  // Countdown timer for rate limit
  useEffect(() => {
    if (backupCountdown <= 0) return;
    const timer = setInterval(() => {
      setBackupCountdown(prev => {
        if (prev <= 1) {
          setBackupStatus(s => s ? { ...s, can_backup: true, seconds_remaining: 0 } : s);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [backupCountdown]);

  const handleBackupDownload = async () => {
    setBackupDownloading(true);
    try {
      const response = await api.get('/api/admin/backup/download', { responseType: 'blob', timeout: 600000 });
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `inception_backup_${new Date().toISOString().slice(0,10)}.gz`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup downloaded successfully');
      loadBackupStatus();
    } catch (e) {
      const detail = e.response?.status === 429
        ? e.response?.data?.detail || 'Rate limited. Please wait before trying again.'
        : e.response?.data?.detail || 'Backup failed';
      toast.error(detail);
      if (e.response?.status === 429) loadBackupStatus();
    }
    setBackupDownloading(false);
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const formatCountdown = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  useEffect(() => {
    api.get('/api/settings').then(r => {
      setSettings(r.data);
      setImportSyncEnabled(r.data.enable_import_sync !== false);
    }).catch(() => {}).finally(() => setLoading(false));
    api.get('/api/seasons').then(r => setSeasons(r.data)).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await api.put('/api/settings', {
        session_timeout_minutes: settings.session_timeout_minutes,
        allow_bulk_delete: settings.allow_bulk_delete,
        enable_email_notifications: settings.enable_email_notifications,
        enable_2fa: settings.enable_2fa,
        email_new_request: settings.email_new_request,
        email_request_reviewed: settings.email_request_reviewed,
        email_cancellation: settings.email_cancellation,
        email_user_invited: settings.email_user_invited,
        email_backup_failure: settings.email_backup_failure,
        email_account_lockout: settings.email_account_lockout,
      });
      setSettings(r.data);
      toast.success(t('settings_saved'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const loadSeasons = async () => {
    const r = await api.get('/api/seasons');
    setSeasons(r.data);
    refreshSeason();
  };

  const createSeason = async () => {
    if (!newSeasonName.trim()) return;
    try {
      await api.post('/api/seasons', { name: newSeasonName.trim() });
      toast.success(`Season '${newSeasonName.trim()}' created and activated`);
      setNewSeasonName('');
      loadSeasons();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create season'); }
  };

  const toggleSeason = async (s) => {
    try {
      if (s.status === 'active') {
        await api.put(`/api/seasons/${s.id}/disable`);
        toast.success(`Season '${s.name}' disabled`);
      } else {
        await api.put(`/api/seasons/${s.id}/activate`);
        toast.success(`Season '${s.name}' activated`);
      }
      loadSeasons();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteSeason = async (s) => {
    try {
      await api.delete(`/api/seasons/${s.id}`);
      toast.success(`Season '${s.name}' archived`);
      loadSeasons();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const toggleImportSync = async () => {
    try {
      const r = await api.post('/api/admin/import-season/toggle');
      setImportSyncEnabled(r.data.enable_import_sync);
      toast.success(`Import & Sync ${r.data.enable_import_sync ? 'enabled' : 'disabled'}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleImportPreview = async () => {
    setImportLoading(true);
    setImportResult(null);
    try {
      const r = await api.post('/api/admin/import-season/preview', { source: importSource });
      setImportPreview(r.data);
      setImportDialogOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load preview'); }
    setImportLoading(false);
  };

  const handleImportApply = async () => {
    setApplying(true);
    try {
      const r = await api.post('/api/admin/import-season/apply', { source: importSource });
      setImportResult(r.data);
      toast.success('Season imported successfully!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
    setApplying(false);
  };

  return (
    <div data-testid="admin-settings-page" className="space-y-3 animate-fade-in">
      <div className="flex items-baseline gap-2">
        <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
          <Settings size={16} /> {t('admin_settings')}
        </h1>
        <p className="text-xs text-gray-400 truncate">{t('admin_settings_desc')}</p>
      </div>

      <Tabs defaultValue="general">
        <TabsList className="bg-gray-100/80 border border-gray-200/60 w-full overflow-x-auto flex flex-nowrap justify-start h-8 p-0.5 gap-0.5">
          <TabsTrigger value="general" className="text-[11px] gap-1 shrink-0 px-2.5 py-1"><Settings size={12} /> {t('general')}</TabsTrigger>
          {hasPerm('manage_seasons') && (
            <TabsTrigger value="seasons" className="text-[11px] gap-1 shrink-0 px-2.5 py-1"><CalendarOff size={12} /> Seasons</TabsTrigger>
          )}
          {(hasPerm('create_users') || hasPerm('edit_users') || hasPerm('delete_users')) && (
            <TabsTrigger value="users" className="text-[11px] gap-1 shrink-0 px-2.5 py-1"><Users size={12} /> {t('users')}</TabsTrigger>
          )}
          {hasPerm('manage_settings') && (
            <TabsTrigger value="notifications" className="text-[11px] gap-1 shrink-0 px-2.5 py-1"><Bell size={12} /> Notifications</TabsTrigger>
          )}
          {(hasPerm('manage_settings') || hasPerm('manage_roles') || hasPerm('view_audit_log')) && (
            <TabsTrigger value="system" className="text-[11px] gap-1 shrink-0 px-2.5 py-1"><Database size={12} /> System</TabsTrigger>
          )}
          {hasPerm('manage_sessions') && (
            <TabsTrigger value="sessions" className="text-[11px] gap-1 shrink-0 px-2.5 py-1"><Wifi size={12} /> Sessions</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="general" className="mt-3 max-w-2xl">
          {loading ? (
            <div className="text-center py-12 text-gray-400 text-sm">{t('loading')}</div>
          ) : (
            <div className="space-y-6">
              <Card className="bg-white border-gray-200/80 shadow-none">
                <CardHeader className="p-4 pb-2">
                  <CardTitle className="section-title flex items-center gap-2">
                    <Clock size={16} /> {t('session_settings')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0 space-y-4">
                  <div>
                    <Label className="text-sm text-gray-600 dark:text-gray-400">{t('session_timeout')}</Label>
                    <p className="text-xs text-gray-400 mb-3">{t('session_timeout_desc')}</p>
                    <div className="flex items-center gap-4">
                      <Slider
                        data-testid="session-timeout-slider"
                        value={[settings.session_timeout_minutes]}
                        onValueChange={([v]) => setSettings(p => ({ ...p, session_timeout_minutes: v }))}
                        min={5} max={120} step={5}
                        className="flex-1"
                      />
                      <div className="flex items-center gap-1.5 min-w-[80px]">
                        <Input
                          data-testid="session-timeout-input"
                          type="number" min={5} max={480}
                          value={settings.session_timeout_minutes}
                          onChange={e => setSettings(p => ({ ...p, session_timeout_minutes: parseInt(e.target.value) || 15 }))}
                          className="w-16 h-8 text-sm text-center bg-gray-50 border-gray-200"
                        />
                        <span className="text-xs text-gray-400">{t('minutes')}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-gray-200/80 shadow-none">
                <CardHeader className="p-4 pb-2">
                  <CardTitle className="section-title flex items-center gap-2">
                    <ShieldCheck size={16} /> Two-Factor Authentication
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm text-gray-600 dark:text-gray-400">Enable 2FA on Login</Label>
                      <p className="text-xs text-gray-400 mt-0.5">Require email verification code after password login</p>
                    </div>
                    <button data-testid="2fa-toggle" onClick={() => setSettings(p => ({ ...p, enable_2fa: !p.enable_2fa }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.enable_2fa ? 'bg-emerald-500' : 'bg-gray-200'}`}>
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${settings.enable_2fa ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-gray-200/80 shadow-none">
                <CardHeader className="p-4 pb-2">
                  <CardTitle className="section-title flex items-center gap-2">
                    {theme === 'dark' ? <Moon size={16} /> : <Sun size={16} />} {t('appearance', 'Appearance')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm text-gray-600 dark:text-gray-400">{t('dark_mode', 'Dark Mode')}</Label>
                      <p className="text-xs text-gray-400 mt-0.5">{t('dark_mode_desc', 'Switch between light and dark theme')}</p>
                    </div>
                    <button
                      data-testid="dark-mode-toggle"
                      onClick={toggleTheme}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${theme === 'dark' ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-gray-200/80 shadow-none">
                <CardHeader className="p-4 pb-2">
                  <CardTitle className="section-title flex items-center gap-2">
                    <Trash2 size={16} /> {t('bulk_delete', 'Bulk Delete')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm text-gray-600 dark:text-gray-400">{t('allow_bulk_delete', 'Allow Bulk Delete')}</Label>
                      <p className="text-xs text-gray-400 mt-0.5">Enable multi-select and bulk delete on Clubs and Matches pages</p>
                    </div>
                    <button
                      data-testid="bulk-delete-toggle"
                      onClick={() => setSettings(p => ({ ...p, allow_bulk_delete: !p.allow_bulk_delete }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.allow_bulk_delete ? 'bg-red-500' : 'bg-gray-200'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${settings.allow_bulk_delete ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                </CardContent>
              </Card>

              <Button data-testid="save-settings-btn" onClick={handleSave} disabled={saving}
                className="bg-gray-900 hover:bg-gray-800 text-sm h-9">
                <Save size={14} className="mr-1.5" /> {saving ? '...' : t('save_settings')}
              </Button>

              <AboutPanel />
            </div>
          )}
        </TabsContent>

        <TabsContent value="users" className="mt-3 [&_h1]:text-base [&_h1]:font-semibold">
          <UsersPage />
        </TabsContent>

        {/* === SYSTEM (nested: Database · Translations · Roles · Audit Log · Sessions) === */}
        <TabsContent value="system" className="mt-3 [&_h1]:text-base [&_h1]:font-semibold [&_h2]:text-sm">
          <Tabs defaultValue={hasPerm('manage_settings') ? 'database' : 'roles'}>
            <TabsList className="bg-transparent border-b border-gray-200/70 dark:border-gray-800 w-full rounded-none justify-start h-8 p-0 gap-1">
              {hasPerm('manage_settings') && (
                <TabsTrigger value="database" data-testid="system-tab-database" className="text-[11px] gap-1 px-2.5 py-1 rounded-md"><Database size={12} /> Database</TabsTrigger>
              )}
              {hasPerm('manage_settings') && (
                <TabsTrigger value="translations" data-testid="system-tab-translations" className="text-[11px] gap-1 px-2.5 py-1 rounded-md"><Languages size={12} /> {t('translations_label')}</TabsTrigger>
              )}
              {hasPerm('manage_roles') && (
                <TabsTrigger value="roles" data-testid="system-tab-roles" className="text-[11px] gap-1 px-2.5 py-1 rounded-md"><Lock size={12} /> {t('roles_permissions', 'Roles')}</TabsTrigger>
              )}
              {hasPerm('view_audit_log') && (
                <TabsTrigger value="audit" data-testid="system-tab-audit" className="text-[11px] gap-1 px-2.5 py-1 rounded-md"><History size={12} /> {t('audit_log')}</TabsTrigger>
              )}
              {hasPerm('edit_sponsors') && (
                <TabsTrigger value="danger" data-testid="system-tab-danger" className="text-[11px] gap-1 px-2.5 py-1 rounded-md text-red-600 dark:text-red-400 data-[state=active]:text-red-700 dark:data-[state=active]:text-red-300"><AlertTriangle size={12} /> Danger Zone</TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="database" className="mt-3 max-w-2xl">
              <DatabaseBackupTab
                backupStatus={backupStatus}
                backupLoading={backupLoading}
                backupDownloading={backupDownloading}
                backupCountdown={backupCountdown}
                loadBackupStatus={loadBackupStatus}
                handleBackupDownload={handleBackupDownload}
                formatBytes={formatBytes}
                formatCountdown={formatCountdown}
              />
            </TabsContent>
            <TabsContent value="translations" className="mt-3">
              <TranslationsPage />
            </TabsContent>
            <TabsContent value="roles" className="mt-3">
              <RolesPage />
            </TabsContent>
            <TabsContent value="audit" className="mt-3">
              <AuditLogPage />
            </TabsContent>
            <TabsContent value="danger" className="mt-3 max-w-2xl">
              <Card className="bg-white dark:bg-gray-900 border-red-200/80 dark:border-red-900/50 shadow-none">
                <CardHeader className="p-4 pb-2">
                  <CardTitle className="text-sm font-medium text-red-700 dark:text-red-300 flex items-center gap-2">
                    <AlertTriangle size={16} /> Danger Zone
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0 space-y-3">
                  <p className="text-xs text-gray-400">These actions change existing data in bulk and cannot be undone from the UI.</p>
                  <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-3 space-y-2">
                    <div>
                      <Label className="text-sm text-gray-700 dark:text-gray-200">Clean up archived sponsors</Label>
                      <p className="text-xs text-gray-400 mt-1">
                        A one-off backfill for sponsors that were archived or deactivated <b>before</b> the automatic cascade existed.
                        For every archived/inactive sponsor it cancels their still-open requests, archives their right assignments, and
                        removes them from any SPO Viewer's list. <b>Nothing is deleted</b> — history, sponsors and usage stay intact.
                      </p>
                    </div>
                    <Button
                      data-testid="cleanup-archived-btn"
                      variant="outline"
                      size="sm"
                      className="text-xs h-8 px-3 border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950"
                      onClick={() => setCleanupConfirm(true)}
                    >
                      Run Cleanup
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </TabsContent>

        {hasPerm('manage_sessions') && (
        <TabsContent value="sessions" className="mt-3 [&_h1]:text-base [&_h1]:font-semibold">
          <ActiveSessionsPage />
        </TabsContent>
        )}

        <TabsContent value="seasons" className="mt-3 max-w-2xl">
          <div className="space-y-4">
            <div>
              <h3 className="section-title">Season Management</h3>
              <p className="text-xs text-gray-400 mt-1">Create and manage seasons. Only one season can be active at a time. Disabling a season hides all its matches and requests from every page.</p>
            </div>
            <div className="flex gap-2">
              <Input data-testid="new-season-input" placeholder="e.g. 2027/28" value={newSeasonName} onChange={e => setNewSeasonName(e.target.value)}
                className="bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 h-8 text-sm flex-1" onKeyDown={e => e.key === 'Enter' && createSeason()} />
              <Button data-testid="create-season-btn" size="sm" onClick={createSeason} disabled={!newSeasonName.trim()}
                className="bg-gray-800 hover:bg-gray-700 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 text-white text-xs h-8 px-3">Create Season</Button>
            </div>
            {seasons.length > 0 && (
              <div className="space-y-2">
                {seasons.filter(s => s.status !== 'archived').map(s => (
                  <div key={s.id} data-testid={`season-row-${s.id}`} className={`flex items-center justify-between px-3 py-2.5 rounded-lg border ${s.status === 'active' ? 'bg-emerald-50 dark:bg-emerald-950 border-emerald-200 dark:border-emerald-800' : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'}`}>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${s.status === 'active' ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{s.name}</span>
                      {s.status === 'active' && <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold uppercase">Active</span>}
                      {s.status === 'disabled' && <span className="text-[10px] text-gray-400 font-semibold uppercase">Disabled</span>}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Button data-testid={`toggle-season-${s.id}`} variant="ghost" size="sm" onClick={() => toggleSeason(s)}
                        className={`text-xs h-7 px-2 ${s.status === 'active' ? 'text-red-500 hover:text-red-700' : 'text-emerald-600 hover:text-emerald-800'}`}>
                        {s.status === 'active' ? 'Disable' : 'Activate'}
                      </Button>
                      {s.status !== 'active' && (
                        <Button variant="ghost" size="sm" onClick={() => deleteSeason(s)} className="text-xs h-7 px-2 text-gray-400 hover:text-amber-600">Archive</Button>
                      )}
                    </div>
                  </div>
                ))}
                {seasons.filter(s => s.status === 'archived').length > 0 && (
                  <div className="pt-2 border-t border-gray-100 dark:border-gray-800">
                    <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider mb-2">Archived</p>
                    {seasons.filter(s => s.status === 'archived').map(s => (
                      <div key={s.id} className="flex items-center justify-between px-3 py-2 rounded-lg opacity-50 hover:opacity-80 transition-opacity">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-gray-300" />
                          <span className="text-sm text-gray-400">{s.name}</span>
                          <span className="text-[10px] text-gray-400 font-semibold uppercase">Archived</span>
                        </div>
                        <Button data-testid={`unarchive-season-${s.id}`} variant="ghost" size="sm" onClick={async () => {
                          try {
                            await api.put(`/api/seasons/${s.id}/unarchive`);
                            toast.success(`Season '${s.name}' unarchived`);
                            loadSeasons();
                          } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                        }} className="text-xs h-7 px-2 text-blue-500 hover:text-blue-700">Unarchive</Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {seasons.length === 0 && <p className="text-xs text-gray-400 text-center py-4">No seasons created yet. Create your first season above.</p>}

            {/* Import & Sync - moved from General */}
            <Card className="bg-white border-gray-200/80 shadow-none mt-4">
              <CardHeader className="p-4 pb-2">
                <CardTitle className="section-title flex items-center gap-2">
                  <Download size={16} /> Season Import & Sync
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm text-gray-600 dark:text-gray-400">Enable Import & Sync</Label>
                    <p className="text-xs text-gray-400 mt-0.5">When disabled, no one can import or sync season data</p>
                  </div>
                  <button
                    data-testid="toggle-import-sync"
                    onClick={toggleImportSync}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${importSyncEnabled ? 'bg-emerald-500' : 'bg-gray-200'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${importSyncEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>

                {importSyncEnabled && (<>
                <div className="border-t border-gray-100 pt-3">
                  <Label className="text-sm text-gray-600 dark:text-gray-400">Import 2026-2027 Season</Label>
                  <p className="text-xs text-gray-400 mt-0.5">Import matches, update schedule data, and manage clubs. Preview all changes before applying.</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500 mb-1.5 block">Data Source</Label>
                  <div className="flex gap-2">
                    <button
                      data-testid="source-spl-btn"
                      onClick={() => setImportSource('spl')}
                      className={`flex-1 px-3 py-2.5 rounded-lg border text-xs font-medium transition-all ${
                        importSource === 'spl'
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400'
                          : 'border-gray-200 text-gray-500 hover:border-gray-300 dark:border-gray-700'
                      }`}
                    >
                      <div className="font-semibold text-sm">SPL Official</div>
                      <div className="text-[10px] mt-0.5 opacity-70">Live data — dates, times, stadiums</div>
                    </button>
                    <button
                      data-testid="source-pdf-btn"
                      onClick={() => setImportSource('pdf')}
                      className={`flex-1 px-3 py-2.5 rounded-lg border text-xs font-medium transition-all ${
                        importSource === 'pdf'
                          ? 'border-amber-500 bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-400'
                          : 'border-gray-200 text-gray-500 hover:border-gray-300 dark:border-gray-700'
                      }`}
                    >
                      <div className="font-semibold text-sm">PDF Schedule</div>
                      <div className="text-[10px] mt-0.5 opacity-70">Official confirmed fixtures</div>
                    </button>
                    <button
                      data-testid="source-espn-btn"
                      onClick={() => setImportSource('espn')}
                      className={`flex-1 px-3 py-2.5 rounded-lg border text-xs font-medium transition-all ${
                        importSource === 'espn'
                          ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-400'
                          : 'border-gray-200 text-gray-500 hover:border-gray-300 dark:border-gray-700'
                      }`}
                    >
                      <div className="font-semibold text-sm">ESPN</div>
                      <div className="text-[10px] mt-0.5 opacity-70">Secondary source</div>
                    </button>
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button data-testid="import-season-btn" size="sm" onClick={handleImportPreview} disabled={importLoading}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8 px-4">
                    {importLoading ? <><Loader2 size={13} className="mr-1 animate-spin" /> Fetching...</> : <><Download size={13} className="mr-1" /> Preview Import</>}
                  </Button>
                </div>
                </>)}
              </CardContent>
            </Card>

            {/* Data maintenance moved to System → Danger Zone */}
          </div>
        </TabsContent>

        <TabsContent value="notifications" className="mt-3 max-w-2xl">
          {loading ? (
            <div className="text-center py-12 text-gray-400 text-sm">{t('loading')}</div>
          ) : (
            <div className="space-y-4">
              <div>
                <h3 className="section-title">Email Notifications</h3>
                <p className="text-xs text-gray-400 mt-1">Control which email notifications are sent and to whom.</p>
              </div>

              <Card className="bg-white dark:bg-gray-900 border-gray-200/80 dark:border-gray-800 shadow-none">
                <CardHeader className="p-4 pb-2">
                  <CardTitle className="section-title flex items-center gap-2">
                    <Mail size={16} /> Email Delivery
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm text-gray-600 dark:text-gray-400">Master Toggle</Label>
                      <p className="text-xs text-gray-400 mt-0.5">Enable or disable all email notifications at once</p>
                    </div>
                    <button
                      data-testid="email-notifications-toggle"
                      onClick={() => setSettings(p => ({ ...p, enable_email_notifications: !p.enable_email_notifications }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.enable_email_notifications ? 'bg-emerald-500' : 'bg-gray-200'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${settings.enable_email_notifications ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>

                  {settings.enable_email_notifications && (
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 space-y-3">
                      <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Per-Event Controls</p>
                      {[
                        { key: 'email_new_request', label: 'New Request Submitted', to: 'Account Managers & Admins' },
                        { key: 'email_request_reviewed', label: 'Request Reviewed', to: 'Sponsor' },
                        { key: 'email_cancellation', label: 'Cancellation Events', to: 'Sponsor / Account Managers' },
                        { key: 'email_user_invited', label: 'User Invited', to: 'New User (with credentials)' },
                        { key: 'email_backup_failure', label: 'Backup Failure Alert', to: 'All Admins' },
                        { key: 'email_account_lockout', label: 'Account Locked Alert', to: 'All Admins' },
                      ].map(item => (
                        <div key={item.key} className="flex items-center justify-between">
                          <div className="flex-1">
                            <span className="text-xs text-gray-700 dark:text-gray-300 font-medium">{item.label}</span>
                            <span className="text-[10px] text-gray-400 ml-2">{item.to}</span>
                          </div>
                          <button
                            data-testid={`toggle-${item.key}`}
                            onClick={() => setSettings(p => ({ ...p, [item.key]: !p[item.key] }))}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${settings[item.key] !== false ? 'bg-emerald-500' : 'bg-gray-200'}`}
                          >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${settings[item.key] !== false ? 'translate-x-4' : 'translate-x-0.5'}`} />
                          </button>
                        </div>
                      ))}
                      <p className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-1">2FA codes & password reset emails are always sent regardless of these settings.</p>
                    </div>
                  )}

                  <div className="border-t border-gray-100 dark:border-gray-800 pt-3">
                    <Label className="text-xs text-gray-500 dark:text-gray-400 mb-1.5 block">Send Test Email</Label>
                    <div className="flex gap-2">
                      <Input data-testid="test-email-input" type="email" placeholder="email@example.com" value={testEmail} onChange={e => setTestEmail(e.target.value)} className="bg-gray-50 border-gray-200 h-8 text-sm flex-1" />
                      <Button data-testid="send-test-email-btn" size="sm" disabled={!testEmail || sendingTest}
                        onClick={async () => {
                          setSendingTest(true);
                          try {
                            const r = await api.post('/api/settings/test-email', { to_email: testEmail });
                            toast.success(r.data.message || 'Test email sent!');
                            if (r.data.preview) toast.info('Preview mode — email was skipped but config looks OK');
                          } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send test email'); }
                          setSendingTest(false);
                        }}
                        className="bg-gray-800 hover:bg-gray-700 text-white text-xs h-8 px-3"
                      >
                        {sendingTest ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} className="mr-1" />}
                        {sendingTest ? 'Sending...' : 'Test'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Button data-testid="save-notification-settings-btn" onClick={handleSave} disabled={saving}
                className="bg-gray-900 hover:bg-gray-800 text-sm h-9">
                <Save size={14} className="mr-1.5" /> {saving ? '...' : t('save_settings')}
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Cleanup confirmation */}
      <Dialog open={cleanupConfirm} onOpenChange={o => { if (!o) setCleanupConfirm(false); }}>
        <DialogContent data-testid="cleanup-confirm-dialog" className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="section-title flex items-center gap-2">
              <AlertTriangle size={16} className="text-red-600" /> Run cleanup on archived sponsors?
            </DialogTitle>
          </DialogHeader>
          <div className="text-xs text-gray-500 dark:text-gray-400 space-y-2">
            <p>For every sponsor currently archived or inactive this will:</p>
            <ul className="list-disc ps-5 space-y-1">
              <li>Cancel their requests that are still open (reason: retroactive cleanup)</li>
              <li>Archive their right assignments</li>
              <li>Remove them from every SPO Viewer's sponsor list</li>
            </ul>
            <p className="text-gray-400">Nothing is deleted, but the cancellations cannot be reversed from the UI. Take a backup first if you are unsure.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCleanupConfirm(false)} className="text-xs border-gray-200 dark:border-gray-700">Cancel</Button>
            <Button data-testid="cleanup-confirm-btn" onClick={runCleanup} disabled={cleanupRunning} className="text-xs bg-red-600 hover:bg-red-700 text-white">
              {cleanupRunning ? 'Running…' : 'Run cleanup'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Season Import Preview Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Download size={18} /> Import Season {importPreview?.season}
            </DialogTitle>
          </DialogHeader>

          {importResult ? (
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-2 text-emerald-600">
                <CheckCircle2 size={20} /> <span className="font-semibold">Import Complete</span>
              </div>
              <div className="space-y-1.5">
                {importResult.changes?.map((c, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle2 size={14} className="text-emerald-500 mt-0.5 shrink-0" />
                    <span>{c}</span>
                  </div>
                ))}
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <span className="font-medium text-gray-700">Total matches in season: </span>
                <span className="font-bold text-emerald-600">{importResult.total_season_matches}</span>
              </div>
              <DialogFooter>
                <Button onClick={() => { setImportDialogOpen(false); setImportResult(null); setImportPreview(null); }}>Done</Button>
              </DialogFooter>
            </div>
          ) : importPreview ? (
            <div className="space-y-4 py-2">
              {/* Summary stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-700">{importPreview.new_matches_total}</div>
                  <div className="text-xs text-blue-600">New Matches</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-amber-700">{importPreview.updates?.length || 0}</div>
                  <div className="text-xs text-amber-600">Updates</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-gray-700">{importPreview.existing_season_matches}</div>
                  <div className="text-xs text-gray-500">Already Exist</div>
                </div>
              </div>

              {/* Source info */}
              <div className="text-xs text-gray-500 bg-gray-50 dark:bg-gray-800 px-3 py-2 rounded flex items-center gap-2">
                Source: <span className="font-semibold text-gray-700 dark:text-gray-300">{importPreview.source_label}</span>
                <span className="text-gray-400">({importPreview.source_matches_fetched} matches fetched)</span>
              </div>

              {importPreview.new_matches_total === 0 && (importPreview.updates?.length || 0) === 0 && importPreview.new_clubs.length === 0 && importPreview.relegated_clubs.length === 0 && (importPreview.duplicates?.length || 0) === 0 ? (
                <div className="flex items-center gap-2 bg-emerald-50 p-3 rounded-lg text-sm text-emerald-700">
                  <CheckCircle2 size={16} /> Season is already fully imported. Nothing to change.
                </div>
              ) : (
                <>
                  {/* Duplicates warning */}
                  {importPreview.duplicates?.length > 0 && (
                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold text-red-700 flex items-center gap-1.5">
                        <AlertTriangle size={14} /> Duplicates Found ({importPreview.duplicates.length})
                      </h3>
                      <div className="space-y-1">
                        {importPreview.duplicates.map((d, i) => (
                          <div key={i} className="bg-red-50 border border-red-200 rounded px-3 py-2 text-xs text-red-700">
                            {d.match} — <span className="font-semibold">{d.count} copies</span> (will remove extras on import)
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Club changes */}
                  {(importPreview.new_clubs.length > 0 || importPreview.relegated_clubs.length > 0) && (
                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold text-gray-700">Club Changes</h3>
                      {importPreview.new_clubs.map((c, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded">
                          <span className="font-medium">+ {c}</span>
                        </div>
                      ))}
                      {importPreview.relegated_clubs.map((c, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 px-3 py-1.5 rounded">
                          <AlertTriangle size={13} /> <span className="font-medium">{c}</span> → Relegated
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Old matches tagging */}
                  {importPreview.old_matches_to_tag > 0 && (
                    <div className="text-sm text-gray-500 bg-gray-50 px-3 py-2 rounded">
                      {importPreview.old_matches_to_tag} existing matches will be tagged as season 2025-2026
                    </div>
                  )}

                  {/* Match updates - detailed field-by-field */}
                  {importPreview.updates?.length > 0 && (
                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold text-gray-700">Match Updates ({importPreview.updates.length})</h3>
                      <div className="max-h-[250px] overflow-y-auto space-y-2">
                        {importPreview.updates.map((u, i) => (
                          <div key={i} className="bg-amber-50 border border-amber-200 rounded px-3 py-2.5">
                            <div className="font-semibold text-gray-800 text-xs mb-1.5">{u.match}</div>
                            <div className="space-y-1">
                              {u.changes.map((c, j) => (
                                typeof c === 'string' ? (
                                  <div key={j} className="text-xs text-amber-700">{c}</div>
                                ) : (
                                  <div key={j} className="flex items-center gap-2 text-xs">
                                    <span className="font-medium text-gray-600 w-16 shrink-0">{c.field}:</span>
                                    <span className="text-red-500 line-through">{c.from || '—'}</span>
                                    <span className="text-gray-400">→</span>
                                    <span className="text-emerald-700 font-medium">{c.to}</span>
                                  </div>
                                )
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* New matches by round */}
                  {importPreview.new_matches_total > 0 && (
                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold text-gray-700">New Matches ({importPreview.new_matches_total})</h3>
                      <div className="grid grid-cols-6 gap-1.5 text-xs">
                        {Object.entries(importPreview.new_matches_by_round).sort((a,b) => parseInt(a[0]) - parseInt(b[0])).map(([round, count]) => (
                          <div key={round} className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-center font-medium">
                            R{round}: {count}
                          </div>
                        ))}
                      </div>
                      {/* Detailed new matches list */}
                      {importPreview.new_matches_detail?.length > 0 && (
                        <div className="max-h-[150px] overflow-y-auto space-y-1">
                          {importPreview.new_matches_detail.map((m, i) => (
                            <div key={i} className="bg-blue-50 border border-blue-100 rounded px-3 py-1.5 text-xs text-blue-800 flex items-center justify-between">
                              <span className="font-medium">R{m.round}: {m.home} vs {m.away}</span>
                              {m.date && <span className="text-blue-500">{m.date}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setImportDialogOpen(false)} className="border-gray-300">Cancel</Button>
                {(importPreview.new_matches_total > 0 || importPreview.new_clubs.length > 0 || importPreview.relegated_clubs.length > 0 || (importPreview.updates?.length || 0) > 0 || (importPreview.duplicates?.length || 0) > 0) ? (
                  <Button data-testid="confirm-import-btn" onClick={handleImportApply} disabled={applying}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    {applying ? <><Loader2 size={14} className="mr-1 animate-spin" /> Importing...</> : <><CheckCircle2 size={14} className="mr-1" /> Confirm Import</>}
                  </Button>
                ) : null}
              </DialogFooter>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}


function AboutPanel() {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    api.get('/api/version').then(r => setInfo(r.data)).catch(() => {});
  }, []);

  if (!info) return null;

  const uptime = () => {
    const s = info.uptime_seconds;
    if (s == null) return null;
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    return d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  return (
    <Card className="bg-white dark:bg-gray-900 border-gray-200/80 dark:border-gray-800 shadow-none mt-2" data-testid="about-panel">
      <CardHeader className="p-4 pb-2">
        <CardTitle className="section-title flex items-center gap-2">
          <Info size={16} /> About
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0 space-y-3">
        <div className="flex items-baseline gap-2.5">
          <span className="text-2xl font-light tracking-tight text-gray-800 dark:text-gray-100" data-testid="about-version">v{info.version}</span>
          <span className="text-xs text-gray-400">released {new Date(info.release_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
        </div>

        {info.server_started_at && (
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 space-y-1.5" data-testid="about-server-info">
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-gray-500">Server started</span>
              <span className="text-[11px] font-medium text-gray-800 dark:text-gray-200">
                {new Date(info.server_started_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-gray-500">Uptime</span>
              <span className="text-[11px] font-medium text-gray-800 dark:text-gray-200">{uptime()}</span>
            </div>
            <p className="text-[10px] text-gray-400 pt-1 leading-relaxed">
              If the version above looks newer than the start time, the latest deploy has not gone live yet.
            </p>
          </div>
        )}

        {info.release_notes?.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">In this release</p>
            <ul className="space-y-1">
              {info.release_notes.map((n, i) => (
                <li key={i} className="text-[11px] text-gray-600 dark:text-gray-400 flex gap-2">
                  <span className="text-gray-300 dark:text-gray-600 shrink-0">—</span>
                  <span>{n}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function GdriveSetupForm({ onSaved, replacing = false }) {
  const [json, setJson] = useState('');
  const [folderId, setFolderId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const r = await api.put('/api/admin/backup/gdrive/config', {
        service_account_json: json,
        folder_id: folderId,
      });
      toast.success(`Connected to "${r.data.folder_name}"`);
      setJson('');
      setFolderId('');
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not save the credentials');
    }
    setSaving(false);
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-3" data-testid="gdrive-setup-form">
      {!replacing && (
        <div className="flex items-start gap-2">
          <AlertTriangle size={14} className="text-amber-500 shrink-0 mt-0.5" />
          <p className="text-[11px] text-gray-600 dark:text-gray-400 leading-relaxed">
            Paste your Google service account key and the Shared Drive folder ID. The key is
            encrypted before it is stored and is never shown again.
          </p>
        </div>
      )}

      <div className="space-y-1.5">
        <Label className="text-[11px] text-gray-500">Service account key (JSON)</Label>
        <textarea
          data-testid="gdrive-json-input"
          value={json}
          onChange={e => setJson(e.target.value)}
          rows={4}
          spellCheck={false}
          placeholder='{ "type": "service_account", "project_id": ... }'
          className="w-full text-[11px] font-mono rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2.5 py-2 text-gray-800 dark:text-gray-200 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400"
        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-[11px] text-gray-500">Shared Drive folder ID (or paste the folder URL)</Label>
        <Input
          data-testid="gdrive-folder-input"
          value={folderId}
          onChange={e => setFolderId(e.target.value)}
          placeholder="1XXCaDZfzt7rgRjYK0YmaXHnvOvPKcy7K"
          className="h-8 text-xs font-mono bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700"
        />
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-md p-2.5" data-testid="gdrive-setup-error">
          <p className="text-[11px] text-red-700 dark:text-red-300 leading-relaxed">{error}</p>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button data-testid="gdrive-save-config-btn" size="sm" onClick={save} disabled={saving}
          className="bg-gray-900 hover:bg-gray-800 text-xs h-8">
          {saving ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <CheckCircle2 size={13} className="mr-1.5" />}
          {saving ? 'Checking...' : 'Save & Connect'}
        </Button>
        <p className="text-[10px] text-gray-400">We test the connection before saving.</p>
      </div>
    </div>
  );
}

function DatabaseBackupTab({ backupStatus, backupDownloading, backupCountdown, loadBackupStatus, handleBackupDownload, formatBytes, formatCountdown }) {  const [loaded, setLoaded] = useState(false);
  const [togglingAuto, setTogglingAuto] = useState(false);
  const [togglingCloud, setTogglingCloud] = useState(false);
  const [togglingGdrive, setTogglingGdrive] = useState(false);
  const [testingGdrive, setTestingGdrive] = useState(false);
  const [gdriveTest, setGdriveTest] = useState(null);
  const [uploadingGdrive, setUploadingGdrive] = useState(false);
  const [changingKey, setChangingKey] = useState(false);
  const [deletingFile, setDeletingFile] = useState(null);

  useEffect(() => {
    if (!loaded) {
      loadBackupStatus();
      setLoaded(true);
    }
  }, [loaded, loadBackupStatus]);

  const last = backupStatus?.last_backup;
  const canBackup = backupStatus?.can_backup !== false;
  const autoEnabled = backupStatus?.auto_backup_enabled || false;
  const scheduledBackups = backupStatus?.scheduled_backups || [];
  const cloudBackup = backupStatus?.cloud_backup || {};
  const gdrive = backupStatus?.gdrive_backup || {};

  const handleToggleAuto = async () => {
    setTogglingAuto(true);
    try {
      const r = await api.put('/api/admin/backup/auto');
      toast.success(`Auto-backup ${r.data.enabled ? 'enabled — first backup starting now' : 'disabled'}`);
      setTimeout(loadBackupStatus, 2000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setTogglingAuto(false);
  };

  const handleToggleCloud = async (provider) => {
    setTogglingCloud(true);
    try {
      const r = await api.put('/api/admin/backup/cloud', { provider });
      toast.success(`Cloud backup ${r.data.enabled ? 'enabled' : 'disabled'} (${r.data.provider})`);
      setTimeout(loadBackupStatus, 1000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to toggle cloud backup'); }
    setTogglingCloud(false);
  };

  const handleTestGdrive = async () => {
    setTestingGdrive(true);
    setGdriveTest(null);
    try {
      const r = await api.get('/api/admin/backup/gdrive/test');
      setGdriveTest({ ok: true, ...r.data });
      toast.success(`Connected to "${r.data.folder_name}"`);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Connection test failed';
      setGdriveTest({ ok: false, error: msg });
      toast.error(msg);
    }
    setTestingGdrive(false);
  };

  const handleToggleGdrive = async () => {
    setTogglingGdrive(true);
    try {
      const r = await api.put('/api/admin/backup/gdrive', { enabled: !gdrive.enabled });
      toast.success(`Google Drive backup ${r.data.enabled ? 'enabled' : 'disabled'}`);
      setTimeout(loadBackupStatus, 1000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to toggle Google Drive backup'); }
    setTogglingGdrive(false);
  };

  const handleUploadToGdrive = async () => {
    setUploadingGdrive(true);
    try {
      const r = await api.post('/api/admin/backup/gdrive/upload', {}, { timeout: 600000 });
      toast.success(`Uploaded ${r.data.filename} (${formatBytes(r.data.size_bytes)}) to Google Drive`);
      loadBackupStatus();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    setUploadingGdrive(false);
  };

  const handleDownloadScheduled = async (filename) => {    try {
      const response = await api.get(`/api/admin/backup/scheduled/${filename}`, { responseType: 'blob', timeout: 600000 });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup downloaded');
    } catch (e) { toast.error(e.response?.data?.detail || 'Download failed'); }
  };

  const handleDeleteScheduled = async (filename) => {
    setDeletingFile(filename);
    try {
      await api.delete(`/api/admin/backup/scheduled/${filename}`);
      toast.success('Backup deleted');
      loadBackupStatus();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
    setDeletingFile(null);
  };

  const timeAgo = (isoStr) => {
    const diff = Date.now() - new Date(isoStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="section-title">Database Backup</h3>
        <p className="text-xs text-gray-400 mt-1">Download a full backup of your database. Each archive is a <code className="text-[10px] bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">.tar.gz</code> containing one JSON file per collection, plus a manifest listing the record counts.</p>
      </div>

      {/* Auto-Backup Card */}
      <Card className="bg-white dark:bg-gray-900 border-gray-200/80 dark:border-gray-800 shadow-none">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="section-title flex items-center gap-2">
            <Clock size={16} /> Scheduled Auto-Backup
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm text-gray-600 dark:text-gray-400">Daily Auto-Backup</Label>
              <p className="text-xs text-gray-400 mt-0.5">Runs every 24 hours, keeps the last {backupStatus?.max_retention || 7} backups</p>
            </div>
            <button
              data-testid="auto-backup-toggle"
              onClick={handleToggleAuto}
              disabled={togglingAuto}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${autoEnabled ? 'bg-emerald-500' : 'bg-gray-200'} ${togglingAuto ? 'opacity-50' : ''}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${autoEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          {autoEnabled && backupStatus?.next_auto_at && (
            <div className="flex items-center gap-2 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-lg px-3 py-2">
              <CheckCircle2 size={14} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
              <span className="text-xs text-emerald-700 dark:text-emerald-300">
                Next auto-backup: <span className="font-semibold">{new Date(backupStatus.next_auto_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
              </span>
            </div>
          )}

          {/* Scheduled Backups List */}
          {scheduledBackups.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Backup History ({scheduledBackups.length}/{backupStatus?.max_retention || 7})</p>
              <div className="space-y-1 max-h-[250px] overflow-y-auto">
                {scheduledBackups.map(b => (
                  <div key={b.filename} className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 group">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <HardDrive size={14} className="text-gray-400 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">{b.filename}</p>
                        <p className="text-[10px] text-gray-400">{formatBytes(b.size_bytes)} · {timeAgo(b.created_at)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        data-testid={`download-scheduled-${b.filename}`}
                        variant="ghost" size="sm"
                        onClick={() => handleDownloadScheduled(b.filename)}
                        className="text-xs h-7 px-2 text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
                      >
                        <Download size={12} />
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => handleDeleteScheduled(b.filename)}
                        disabled={deletingFile === b.filename}
                        className="text-xs h-7 px-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                      >
                        {deletingFile === b.filename ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {autoEnabled && scheduledBackups.length === 0 && (
            <p className="text-xs text-gray-400 text-center py-2">First auto-backup is being created...</p>
          )}
        </CardContent>
      </Card>

      {/* Cloud Backup Card */}
      <Card className="bg-white dark:bg-gray-900 border-gray-200/80 dark:border-gray-800 shadow-none">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="section-title flex items-center gap-2">
            {cloudBackup.enabled ? <Cloud size={16} /> : <CloudOff size={16} />} Cloud Backup
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm text-gray-600 dark:text-gray-400">Auto-Upload to Cloud</Label>
              <p className="text-xs text-gray-400 mt-0.5">Automatically upload each scheduled backup to cloud storage</p>
            </div>
            <button
              data-testid="cloud-backup-toggle"
              onClick={() => handleToggleCloud('emergent')}
              disabled={togglingCloud}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${cloudBackup.enabled ? 'bg-emerald-500' : 'bg-gray-200'} ${togglingCloud ? 'opacity-50' : ''}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${cloudBackup.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          {cloudBackup.enabled && (
            <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={14} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
                <span className="text-xs text-emerald-700 dark:text-emerald-300 font-medium">
                  Connected to Emergent Object Storage
                </span>
              </div>
              {cloudBackup.last_upload_at && (
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-emerald-600/70">Last Upload</span>
                    <span className="text-[10px] font-medium text-emerald-700 dark:text-emerald-300">
                      {new Date(cloudBackup.last_upload_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-emerald-600/70">Size</span>
                    <span className="text-[10px] font-medium text-emerald-700 dark:text-emerald-300">{formatBytes(cloudBackup.last_upload_size)}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          <p className="text-[10px] text-gray-400">
            Requires auto-backup to be enabled. Each scheduled backup will be uploaded automatically after creation.
          </p>
        </CardContent>
      </Card>

      {/* Google Drive Backup Card */}
      <Card className="bg-white dark:bg-gray-900 border-gray-200/80 dark:border-gray-800 shadow-none" data-testid="gdrive-backup-card">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="section-title flex items-center gap-2">
            <Cloud size={16} /> Google Drive Backup
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0 space-y-3">
          {!gdrive.configured ? (
            <GdriveSetupForm onSaved={loadBackupStatus} />
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Copy every backup to Google Drive</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">Runs in addition to cloud storage — keeps the last {gdrive.retention || 7} files</p>
                </div>
                <button
                  data-testid="gdrive-backup-toggle"
                  onClick={handleToggleGdrive}
                  disabled={togglingGdrive}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${gdrive.enabled ? 'bg-emerald-500' : 'bg-gray-200'} ${togglingGdrive ? 'opacity-50' : ''}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${gdrive.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              {gdrive.last_error && (
                <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-3" data-testid="gdrive-last-error">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={14} className="text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                    <p className="text-[10px] text-red-700 dark:text-red-300 leading-relaxed">{gdrive.last_error}</p>
                  </div>
                </div>
              )}

              {gdrive.last_upload_at && (
                <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3" data-testid="gdrive-last-upload">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 size={14} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
                    <span className="text-xs text-emerald-700 dark:text-emerald-300 font-medium">Last upload succeeded</span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-emerald-600/70">When</span>
                      <span className="text-[10px] font-medium text-emerald-700 dark:text-emerald-300">
                        {new Date(gdrive.last_upload_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-emerald-600/70">Size</span>
                      <span className="text-[10px] font-medium text-emerald-700 dark:text-emerald-300">{formatBytes(gdrive.last_upload_size)}</span>
                    </div>
                  </div>
                </div>
              )}

              {gdriveTest?.ok && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 space-y-1" data-testid="gdrive-test-result">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-gray-500">Folder</span>
                    <span className="text-[10px] font-medium text-gray-800 dark:text-gray-200">{gdriveTest.folder_name}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-gray-500">Files in folder</span>
                    <span className="text-[10px] font-medium text-gray-800 dark:text-gray-200">{gdriveTest.existing_backups}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-gray-500">Service account</span>
                    <span className="text-[10px] font-medium text-gray-800 dark:text-gray-200 truncate max-w-[60%]">{gdriveTest.service_account_email}</span>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <Button
                  data-testid="gdrive-test-btn"
                  variant="outline" size="sm"
                  onClick={handleTestGdrive}
                  disabled={testingGdrive}
                  className="text-xs"
                >
                  {testingGdrive ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Wifi size={13} className="mr-1.5" />}
                  {testingGdrive ? 'Testing...' : 'Test Connection'}
                </Button>
                <Button
                  data-testid="gdrive-upload-now-btn"
                  variant="outline" size="sm"
                  onClick={handleUploadToGdrive}
                  disabled={uploadingGdrive}
                  className="text-xs"
                >
                  {uploadingGdrive ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Cloud size={13} className="mr-1.5" />}
                  {uploadingGdrive ? 'Uploading...' : 'Back Up Now'}
                </Button>
                <Button
                  data-testid="gdrive-change-key-btn"
                  variant="ghost" size="sm"
                  onClick={() => setChangingKey(v => !v)}
                  className="text-xs text-gray-500"
                >
                  <Lock size={13} className="mr-1.5" /> {changingKey ? 'Cancel' : 'Replace Key'}
                </Button>
              </div>

              {gdrive.sa_email && (
                <p className="text-[10px] text-gray-400 truncate">Connected as {gdrive.sa_email}</p>
              )}

              {changingKey && (
                <GdriveSetupForm
                  replacing
                  onSaved={() => { setChangingKey(false); loadBackupStatus(); }}
                />
              )}
            </>
          )}

          <p className="text-[10px] text-gray-400">
            The target folder must live inside a Google Workspace Shared Drive, and the service account needs the Content manager role.
          </p>
        </CardContent>
      </Card>

      {/* Manual Backup Card */}
      <Card className="bg-white dark:bg-gray-900 border-gray-200/80 dark:border-gray-800 shadow-none">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="section-title flex items-center gap-2">
            <HardDrive size={16} /> Manual Backup
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0 space-y-3">
          {last && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 space-y-1.5">
              <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Last Manual Backup</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Date</span>
                  <span className="text-xs font-medium text-gray-800 dark:text-gray-200">
                    {new Date(last.completed_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Size</span>
                  <span className="text-xs font-medium text-gray-800 dark:text-gray-200">{formatBytes(last.file_size_bytes)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">By</span>
                  <span className="text-xs font-medium text-gray-800 dark:text-gray-200">{last.triggered_by_name || 'Admin'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Database</span>
                  <span className="text-xs font-medium text-gray-800 dark:text-gray-200">{last.db_name}</span>
                </div>
              </div>
            </div>
          )}

          {!canBackup && backupCountdown > 0 && (
            <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
              <Clock size={14} className="text-amber-600 dark:text-amber-400 shrink-0" />
              <span className="text-xs text-amber-700 dark:text-amber-300">
                Next backup available in <span className="font-bold tabular-nums">{formatCountdown(backupCountdown)}</span>
              </span>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Full database export (all collections, compressed .gz)</p>
              <p className="text-[10px] text-gray-400 mt-0.5">Rate limit: one backup every {backupStatus?.rate_limit_minutes || 15} minutes</p>
            </div>
            <Button
              data-testid="download-backup-btn"
              size="sm"
              disabled={!canBackup || backupDownloading}
              onClick={handleBackupDownload}
              className="bg-gray-800 hover:bg-gray-700 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 text-white text-xs h-8 px-4"
            >
              {backupDownloading ? (
                <><Loader2 size={13} className="mr-1.5 animate-spin" /> Generating...</>
              ) : (
                <><Download size={13} className="mr-1.5" /> Download Backup</>
              )}
            </Button>
          </div>

          <div className="border-t border-gray-100 dark:border-gray-800 pt-3">
            <p className="text-[10px] text-gray-400 leading-relaxed">
              <span className="font-semibold text-gray-500">Restore instructions:</span> Unpack the archive, then load each collection with <code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-[10px]">mongoimport --db=your_db_name --collection=NAME --file=NAME.jsonl</code>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
